import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, GripHorizontal, Settings, FileText, Wrench, Download, Loader2, CheckCircle, AlertCircle, CheckSquare } from 'lucide-react';
import { usePipelineBuilderStore } from '../../store/pipelineBuilderStore';
import { useProcessorPrompts } from '../../hooks/useProcessorPrompts';
import { PromptItem } from './PromptItem';
import { PromptEditorModal } from './PromptEditorModal';
import { PromptData } from '../../types/pipeline';
import ToolSelector from './ToolSelector';
import { TasksEditor } from './TasksEditor';
import { ShellConfigHelper } from './ShellConfigHelper';
import { VectorConfigHelper } from './VectorConfigHelper';

// Get API base URL (matches ApiService configuration)
const getApiBaseUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  if (apiUrl) {
    return `${apiUrl}/api`;
  } else if (process.env.NODE_ENV === "production") {
    return "https://api.roboworks.com/api";
  } else {
    return "http://localhost:8000/api";
  }
};

interface PropertiesPanelProps {
  className?: string;
}

const MIN_HEIGHT = 400;
const MAX_HEIGHT = 1200;

/**
 * A2A Configuration Helper Component (SWRM-133)
 * 
 * Provides a simple UI to fetch external agent cards and auto-populate config.
 */
interface A2AConfigHelperProps {
  onConfigGenerated: (config: any) => void;
  disabled?: boolean;
  selectedNode?: any;
  updateNode?: (nodeId: string, updates: any) => void;
}

const A2AConfigHelper: React.FC<A2AConfigHelperProps> = ({ onConfigGenerated, disabled, selectedNode, updateNode }) => {
  const [endpointUrl, setEndpointUrl] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [fetchStatus, setFetchStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleFetchAgentCard = async () => {
    if (!endpointUrl.trim()) {
      setFetchStatus('error');
      setErrorMessage('Please enter an endpoint URL');
      return;
    }

    setIsFetching(true);
    setFetchStatus('idle');
    setErrorMessage('');

    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(
        `${baseUrl}/a2a/fetch-agent-card?endpoint_url=${encodeURIComponent(endpointUrl.trim())}&timeout=10`
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch agent card' }));
        throw new Error(errorData.detail || 'Failed to fetch agent card');
      }

      const agentCard = await response.json();

      // Parse input schema to determine required and optional inputs
      const inputSchema = agentCard.input_schema;
      const requiredInputs: string[] = [];
      const optionalInputs: string[] = [];

      if (inputSchema && inputSchema.properties) {
        // Get schema-level required array (correct JSON Schema format)
        const requiredFields = inputSchema.required || [];
        Object.entries(inputSchema.properties).forEach(([key, prop]: [string, any]) => {
          if (requiredFields.includes(key)) {
            requiredInputs.push(key);
          } else {
            optionalInputs.push(key);
          }
        });
      }

      // Generate config from agent card
      const config = {
        endpoint_url: agentCard.endpoint || endpointUrl.trim(),
        agent_name: agentCard.name || '',
        auth_type: agentCard.authentication ? (agentCard.authentication.type === 'bearer' ? 'bearer' : 'api_key') : 'none',
        auth_token: '',
        timeout_seconds: 30,
        max_retries: 3,
        input_schema: inputSchema, // Store schema for reference
      };

      // Call the callback to update config AND update node inputs
      onConfigGenerated(config);

      // Also update the node's input keys to show handles
      if (selectedNode && updateNode) {
        updateNode(selectedNode.id, {
          data: {
            ...selectedNode.data,
            config,
            required_input_keys: requiredInputs,
            optional_input_keys: optionalInputs,
          },
        });
      }

      setFetchStatus('success');
      setTimeout(() => setFetchStatus('idle'), 3000);
    } catch (error: any) {
      console.error('Failed to fetch agent card:', error);
      setFetchStatus('error');
      setErrorMessage(error.message || 'Failed to fetch agent card');
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Download className="w-4 h-4 text-blue-600 dark:text-blue-400" />
        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100">
          Fetch Agent Card
        </h4>
      </div>
      
      <p className="text-xs text-blue-700 dark:text-blue-300">
        Enter the external agent's endpoint URL to automatically fetch its configuration
      </p>

      <div className="space-y-2">
        {/* URL input and button side-by-side */}
        <div className="flex gap-2">
          <input
            type="text"
            value={endpointUrl}
            onChange={(e) => setEndpointUrl(e.target.value)}
            placeholder="https://external-agent.example.com"
            disabled={disabled || isFetching}
            className="flex-1 px-3 py-2 border border-blue-300 dark:border-blue-700 rounded-md
              bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm
              disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed
              focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          <button
            onClick={handleFetchAgentCard}
            disabled={disabled || isFetching || !endpointUrl.trim()}
            className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium
              bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors
              disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isFetching ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Fetching...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Fetch
              </>
            )}
          </button>
        </div>

        {fetchStatus === 'success' && (
          <div className="flex items-center gap-2 text-xs text-green-700 dark:text-green-400">
            <CheckCircle className="w-4 h-4" />
            Configuration generated! Review and adjust the JSON below.
          </div>
        )}

        {fetchStatus === 'error' && (
          <div className="flex items-start gap-2 text-xs text-red-700 dark:text-red-400">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{errorMessage}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export const PropertiesPanel: React.FC<PropertiesPanelProps> = ({ className = '' }) => {
  const { 
    selectedNodeId, 
    nodes, 
    updateNode, 
    selectNode, 
    panelHeight, 
    setPanelHeight,
    validatedAt,
  } = usePipelineBuilderStore();
  const selectedNode = nodes.find((n) => n.id === selectedNodeId);
  
  // Resize state
  const [isResizing, setIsResizing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number>(0);
  const startHeightRef = useRef<number>(0);
  
  // Track previous node ID to detect actual node switches (SWRM-129)
  const prevNodeIdRef = useRef<string | null>(null);
  
  // Track desired tab when programmatically opening (SWRM-129)
  const pendingTabRef = useRef<'general' | 'prompts' | 'tools' | 'tasks' | null>(null);

  // Tab state (SWRM-103, SWRM-129)
  const [activeTab, setActiveTab] = useState<'general' | 'prompts' | 'tools' | 'tasks' | 'commands' | 'vector_config'>('general');

  // Local state for form fields
  const [stepId, setStepId] = useState('');
  const [name, setName] = useState('');
  const [onFail, setOnFail] = useState<'PAUSE' | 'CONTINUE' | 'ABORT'>('PAUSE');
  const [configJson, setConfigJson] = useState('{}');

  // LLM Provider state (SWRM-107)
  const [llmProviders, setLlmProviders] = useState<Array<{
    id: string;
    name: string;
    type: string;
    model: string | null;
    enabled: boolean;
    default: boolean;
  }>>([]);
  const [llmProvidersLoading, setLlmProvidersLoading] = useState(false);

  // Validation errors
  const [jsonError, setJsonError] = useState('');

  // Prompts state (SWRM-103)
  const [editingPrompt, setEditingPrompt] = useState<{
    name: string;
    data: PromptData;
    currentContent: string;
  } | null>(null);

  // Fetch processor prompts
  const processorName = selectedNode?.data.processor_name || null;
  const { prompts: processorPrompts, isLoading: promptsLoading } = useProcessorPrompts(processorName);

  // Fetch LLM providers on mount (SWRM-107)
  useEffect(() => {
    const fetchLlmProviders = async () => {
      setLlmProvidersLoading(true);
      try {
        const baseUrl = getApiBaseUrl();
        const response = await fetch(`${baseUrl}/llm/providers`);
        if (!response.ok) {
          throw new Error(`Failed to fetch LLM providers: ${response.statusText}`);
        }
        const data = await response.json();
        setLlmProviders(data);
      } catch (error) {
        console.error('Failed to fetch LLM providers:', error);
        setLlmProviders([]);
      } finally {
        setLlmProvidersLoading(false);
      }
    };

    fetchLlmProviders();
  }, []);

  // Listen for custom event to open specific tab (SWRM-129)
  useEffect(() => {
    const handleOpenTab = (event: CustomEvent) => {
      const { nodeId, tab } = event.detail;
      if (nodeId && nodeId === selectedNodeId) {
        // Node already selected, just switch tab
        setActiveTab(tab);
      } else if (nodeId) {
        // Need to select node first, then switch tab
        // Store the desired tab to apply after node selection
        pendingTabRef.current = tab;
        selectNode(nodeId);
      }
    };

    window.addEventListener('openPropertiesPanelTab', handleOpenTab as EventListener);
    return () => {
      window.removeEventListener('openPropertiesPanelTab', handleOpenTab as EventListener);
    };
  }, [selectedNodeId, selectNode]);

  // Sync local state with selected node
  useEffect(() => {
    if (selectedNode?.data) {
      const currentNodeId = selectedNode.id;
      const isNewNode = prevNodeIdRef.current !== currentNodeId;
      
      setStepId(selectedNode.data.step_id || '');
      setName(selectedNode.data.name || '');
      setOnFail(selectedNode.data.on_fail || 'PAUSE');
      
      // Format JSON for display
      try {
        const config = selectedNode.data.config || {};
        setConfigJson(JSON.stringify(config, null, 2));
      } catch {
        setConfigJson('{}');
      }
      
      // Clear errors when switching nodes
      setJsonError('');
      
      // Handle tab selection (SWRM-129)
      if (isNewNode) {
        // Check if there's a pending tab to open (from programmatic selection)
        if (pendingTabRef.current) {
          setActiveTab(pendingTabRef.current);
          pendingTabRef.current = null; // Clear after using
        } else {
          // Default to general tab for new node selection
          setActiveTab('general');
        }
        prevNodeIdRef.current = currentNodeId;
      }
    }
  }, [selectedNode]);

  // Validation function for JSON
  const validateJson = useCallback((value: string): string | null => {
    if (!value.trim()) {
      return null; // Empty is okay, will default to {}
    }

    try {
      JSON.parse(value);
      return null;
    } catch (error: any) {
      // Try to extract line number from error message
      const lineMatch = error.message.match(/position (\d+)/);
      if (lineMatch) {
        const position = parseInt(lineMatch[1], 10);
        const lines = value.substring(0, position).split('\n');
        return `Invalid JSON at line ${lines.length}`;
      }
      return `Invalid JSON: ${error.message}`;
    }
  }, []);

  // Resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    startYRef.current = e.clientY;
    startHeightRef.current = panelHeight;
    setIsResizing(true);
  }, [panelHeight]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Calculate how much the mouse has moved (negative = moved up = increase height)
      const deltaY = startYRef.current - e.clientY;
      const newHeight = startHeightRef.current + deltaY;
      const clampedHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, newHeight));
      setPanelHeight(clampedHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, setPanelHeight]);

  // Update handlers

  const handleNameChange = useCallback(
    (value: string) => {
      setName(value);
      if (selectedNode) {
        updateNode(selectedNode.id, {
          data: { ...selectedNode.data, name: value },
        });
      }
    },
    [selectedNode, updateNode]
  );

  const handleOnFailChange = useCallback(
    (value: 'PAUSE' | 'CONTINUE' | 'ABORT') => {
      setOnFail(value);
      if (selectedNode) {
        updateNode(selectedNode.id, {
          data: { ...selectedNode.data, on_fail: value },
        });
      }
    },
    [selectedNode, updateNode]
  );

  const handleLlmProviderChange = useCallback(
    (providerId: string) => {
      if (selectedNode) {
        const currentConfig = selectedNode.data.config || {};
        const newConfig = {
          ...currentConfig,
          llm_provider_id: providerId || null,
        };
        
        updateNode(selectedNode.id, {
          data: { ...selectedNode.data, config: newConfig },
        });
        
        // Update config JSON display
        setConfigJson(JSON.stringify(newConfig, null, 2));
      }
    },
    [selectedNode, updateNode]
  );

  const handleConfigBlur = useCallback(() => {
    if (!selectedNode) return;

    const error = validateJson(configJson);
    setJsonError(error || '');

    if (!error) {
      try {
        const parsedConfig = configJson.trim() ? JSON.parse(configJson) : {};
        updateNode(selectedNode.id, {
          data: { ...selectedNode.data, config: parsedConfig },
        });
      } catch {
        // Already validated, shouldn't happen
      }
    }
  }, [selectedNode, configJson, validateJson, updateNode]);

  const handleClose = useCallback(() => {
    selectNode(null);
  }, [selectNode]);

  // Prompt handlers (SWRM-103)
  const handleEditPrompt = useCallback((promptName: string) => {
    if (!processorPrompts || !selectedNode) return;

    const promptData = processorPrompts.prompts[promptName];
    if (!promptData) return;

    // Get current content from config.prompts or use default
    const currentConfig = selectedNode.data.config || {};
    const currentPrompts = currentConfig.prompts || {};
    // Use !== undefined to preserve empty strings
    const currentContent = currentPrompts[promptName] !== undefined ? currentPrompts[promptName] : promptData.content;

    // Include custom inputs as available variables (for dynamic input processors)
    const customInputNames = selectedNode.data.custom_inputs?.map((ci: { name: string; description?: string }) => ci.name) || [];
    
    // Check if processor supports dynamic inputs
    const supportsDynamicInputs = selectedNode.data.config?.supports_dynamic_inputs === true ||
                                   ['oneshot_document', 'agentic_task', 'orchestrator', 'vector', 'shell'].includes(selectedNode.data.processor_name);
    
    // For dynamic input processors: ONLY custom_inputs are valid (input_artifact_keys is just wiring)
    // For regular processors: input_artifact_keys proves the input is connected
    const inputArtifactNames = supportsDynamicInputs ? [] : Object.keys(selectedNode.data.input_artifact_keys || {});
    
    const allVariables = [...promptData.variables, ...customInputNames, ...inputArtifactNames];

    // Mark optional inputs (from processor metadata) as optional
    const optionalInputs = selectedNode.data.optional_input_keys || [];
    // For dynamic input processors, custom inputs are required (user explicitly added them to use in prompt)
    // For traditional processors, custom inputs may be optional
    const optionalVariables = supportsDynamicInputs 
      ? [...optionalInputs] // Dynamic input processors: custom_inputs are required
      : [...optionalInputs, ...customInputNames]; // Traditional: custom inputs are optional

    setEditingPrompt({
      name: promptName,
      data: {
        ...promptData,
        variables: allVariables, // Include custom inputs in available variables
        optional_variables: optionalVariables, // Mark which ones are optional
      },
      currentContent,
    });
  }, [processorPrompts, selectedNode]);

  const handleSavePrompt = useCallback((content: string) => {
    if (!editingPrompt || !selectedNode) return;

    const currentConfig = selectedNode.data.config || {};
    const currentPrompts = currentConfig.prompts || {};

    // Update the prompt
    const updatedPrompts = {
      ...currentPrompts,
      [editingPrompt.name]: content,
    };

    // Update node config
    updateNode(selectedNode.id, {
      data: {
        ...selectedNode.data,
        config: {
          ...currentConfig,
          prompts: updatedPrompts,
        },
      },
    });

    // Also update the configJson display
    const updatedConfig = {
      ...currentConfig,
      prompts: updatedPrompts,
    };
    setConfigJson(JSON.stringify(updatedConfig, null, 2));

    // Trigger auto-revalidation if pipeline was previously validated
    if (validatedAt) {
      setTimeout(async () => {
        try {
          const { buildPipelineSteps } = require('../../utils/executionFlow');
          const { apiService } = require('../../services/api');
          // Get fresh state from store (not from closure)
          const currentState = usePipelineBuilderStore.getState();
          const steps = buildPipelineSteps(currentState.nodes, currentState.edges);
          const results = await apiService.validatePipeline({ steps });
          currentState.setValidationResults(results);
        } catch (error) {
          console.error('Auto-revalidation failed:', error);
        }
      }, 500);
    }

    // Close modal
    setEditingPrompt(null);
  }, [editingPrompt, selectedNode, updateNode, validatedAt]);
  
  const handleDynamicOutputsChange = useCallback((outputs: Array<{name: string, type: string}>) => {
    if (!selectedNode) return;
    
    // Update node data with new dynamic outputs
    // Update BOTH data.dynamic_outputs (for UI) and config.dynamic_outputs (for persistence)
    updateNode(selectedNode.id, {
      data: {
        ...selectedNode.data,
        dynamic_outputs: outputs,
        config: {
          ...selectedNode.data.config,
          dynamic_outputs: outputs,
        },
      },
    });
  }, [selectedNode, updateNode]);

  const handleClosePromptEditor = useCallback(() => {
    setEditingPrompt(null);
  }, []);

  // Get current prompt content for a prompt name
  const getCurrentPromptContent = useCallback((promptName: string): string => {
    if (!selectedNode || !processorPrompts) return '';
    
    const currentConfig = selectedNode.data.config || {};
    const currentPrompts = currentConfig.prompts || {};
    const promptData = processorPrompts.prompts[promptName];
    
    return currentPrompts[promptName] || (promptData?.content || '');
  }, [selectedNode, processorPrompts]);

  // Check if system input node (locked)
  const isLocked = selectedNode?.data.isLocked || false;

  // Hide panel if no node selected
  if (!selectedNode) {
    return null;
  }

  return (
    <div
      ref={panelRef}
      className={`flex flex-col border-t bg-gray-50 dark:bg-gray-900 ${className}`}
      style={{ 
        height: `${panelHeight}px`,
        minHeight: `${MIN_HEIGHT}px`,
        maxHeight: `${MAX_HEIGHT}px`
      }}
    >
      {/* Resize Handle */}
      <div
        onMouseDown={handleResizeStart}
        className={`h-2 bg-gray-200 dark:bg-gray-700 hover:bg-blue-400 dark:hover:bg-blue-600 transition-colors cursor-ns-resize flex items-center justify-center group ${
          isResizing ? 'bg-blue-500 dark:bg-blue-500' : ''
        }`}
        data-testid="resize-handle"
      >
        <GripHorizontal className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-white" />
      </div>
      
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {selectedNode.data.processor_name || 'Step'} Configuration
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Close panel"
              data-testid="close-button"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs (SWRM-103) */}
          <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
            <nav className="flex -mb-px" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('general')}
                className={`flex items-center gap-2 py-2 px-4 border-b-2 font-medium text-sm ${
                  activeTab === 'general'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                }`}
                data-testid="general-tab"
              >
                <Settings className="w-4 h-4" />
                General
              </button>
              <button
                onClick={() => setActiveTab('prompts')}
                className={`flex items-center gap-2 py-2 px-4 border-b-2 font-medium text-sm ${
                  activeTab === 'prompts'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                }`}
                data-testid="prompts-tab"
              >
                <FileText className="w-4 h-4" />
                Prompts
                {processorPrompts && !processorPrompts.has_prompts && (
                  <span className="text-xs text-gray-400">(N/A)</span>
                )}
              </button>
              {/* Tools tab - only for agentic processors */}
              {(selectedNode?.data.processor_name === 'agentic_task' || 
                selectedNode?.data.processor_name === 'orchestrator' ||
                selectedNode?.data.processor_name === 'vector') && (
                <button
                  onClick={() => setActiveTab('tools')}
                  className={`flex items-center gap-2 py-2 px-4 border-b-2 font-medium text-sm ${
                    activeTab === 'tools'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                  }`}
                  data-testid="tools-tab"
                >
                  <Wrench className="w-4 h-4" />
                  Tools
                </button>
              )}
              {/* Tasks tab - only for agentic processors */}
              {(selectedNode?.data.processor_name === 'agentic_task' || 
                selectedNode?.data.processor_name === 'orchestrator') && (
                <button
                  onClick={() => setActiveTab('tasks')}
                  className={`flex items-center gap-2 py-2 px-4 border-b-2 font-medium text-sm ${
                    activeTab === 'tasks'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                  }`}
                  data-testid="tasks-tab"
                >
                  <CheckSquare className="w-4 h-4" />
                  Tasks
                </button>
              )}

              {/* Commands Tab - only for shell processor */}
              {selectedNode?.data.processor_name === 'shell' && (
                <button
                  onClick={() => setActiveTab('commands')}
                  className={`flex items-center gap-2 py-2 px-4 border-b-2 font-medium text-sm ${
                    activeTab === 'commands'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                  }`}
                  data-testid="commands-tab"
                >
                  <FileText className="w-4 h-4" />
                  Commands
                </button>
              )}

              {/* Vector Config Tab - only for vector processor */}
              {selectedNode?.data.processor_name === 'vector' && (
                <button
                  onClick={() => setActiveTab('vector_config')}
                  className={`flex items-center gap-2 py-2 px-4 border-b-2 font-medium text-sm ${
                    activeTab === 'vector_config'
                      ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                  }`}
                  data-testid="vector-config-tab"
                >
                  <FileText className="w-4 h-4" />
                  Config
                </button>
              )}
            </nav>
          </div>

          {/* General Tab Content */}
          {activeTab === 'general' && (
        <div className="space-y-4">
          {/* Step ID */}
          <div>
            <label
              htmlFor="step-id"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Step ID
            </label>
            <input
              id="step-id"
              type="text"
              value={stepId}
              disabled={true}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md font-mono text-sm
                bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white
                cursor-not-allowed
                focus:outline-none"
              data-testid="step-id-input"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Auto-generated unique identifier (read-only)
            </p>
          </div>

          {/* Display Name */}
          <div>
            <label
              htmlFor="display-name"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Display Name
            </label>
            <input
              id="display-name"
              type="text"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed
                focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Generate Pseudocode"
              data-testid="display-name-input"
            />
          </div>

          {/* On Fail */}
          <div>
            <label
              htmlFor="on-fail"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              On Failure
            </label>
            <select
              id="on-fail"
              value={onFail}
              onChange={(e) => handleOnFailChange(e.target.value as 'PAUSE' | 'CONTINUE' | 'ABORT')}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed
                focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="on-fail-select"
            >
              <option value="PAUSE">Pause Pipeline (mark as failed)</option>
              <option value="CONTINUE">Continue to next step</option>
              <option value="ABORT">Abort entire pipeline</option>
            </select>
          </div>

          {/* LLM Provider (SWRM-107) */}
          <div>
            <label
              htmlFor="llm-provider"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              LLM Provider (Optional)
            </label>
            <select
              id="llm-provider"
              value={selectedNode?.data.config?.llm_provider_id || ''}
              onChange={(e) => handleLlmProviderChange(e.target.value)}
              disabled={isLocked || llmProvidersLoading}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed
                focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="llm-provider-select"
            >
              <option value="">
                Use Default{llmProviders.find(p => p.default) 
                  ? ` (${llmProviders.find(p => p.default)!.name})` 
                  : ''}
              </option>
              {llmProviders.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.name} {provider.model ? `(${provider.model})` : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {llmProvidersLoading 
                ? 'Loading providers...' 
                : 'Select a specific LLM provider or use the system default'
              }
            </p>
          </div>

          {/* A2A Agent Helper (SWRM-133) - only show for a2a_agent processor */}
          {selectedNode?.data.processor_name === 'a2a_agent' && (
            <A2AConfigHelper
              onConfigGenerated={(config) => {
                // Update config JSON
                setConfigJson(JSON.stringify(config, null, 2));
                if (selectedNode) {
                  updateNode(selectedNode.id, {
                    data: { ...selectedNode.data, config },
                  });
                }
              }}
              disabled={isLocked}
              selectedNode={selectedNode}
              updateNode={updateNode}
            />
          )}

          {/* Config JSON */}
          <div>
            <label
              htmlFor="config-json"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Configuration (JSON)
            </label>
            <textarea
              id="config-json"
              value={configJson}
              onChange={(e) => setConfigJson(e.target.value)}
              onBlur={handleConfigBlur}
              disabled={isLocked}
              rows={8}
              className={`w-full px-3 py-2 border rounded-md font-mono text-sm
                ${jsonError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}
                bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed
                focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="{}"
              data-testid="config-json-textarea"
            />
            {jsonError && (
              <p className="text-xs text-red-500 mt-1" data-testid="config-json-error">
                {jsonError}
              </p>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Processor-specific settings (e.g., model, temperature, max_tokens)
            </p>
          </div>
        </div>
          )}

          {/* Prompts Tab Content (SWRM-103) */}
          {activeTab === 'prompts' && (
            <div>
              {promptsLoading ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>Loading prompts...</p>
                </div>
              ) : !processorPrompts || !processorPrompts.has_prompts ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">This processor does not use LLM prompts</p>
                  <p className="text-xs mt-1">Only LLM-based processors like pseudocode, ticket, task, and coding have customizable prompts.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Info Banner */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3">
                    <p className="text-sm text-blue-900 dark:text-blue-200">
                      Customize prompts for this specific step. Changes only affect this pipeline step.
                      Use <code className="text-xs bg-blue-100 dark:bg-blue-900 px-1 rounded">{`{{variable}}`}</code> syntax for dynamic content.
                    </p>
                  </div>

                  {/* Prompts organized by phase */}
                  {processorPrompts.phases.map((phase) => (
                    <div key={phase.name} className="space-y-3">
                      <div className="border-l-4 border-blue-500 pl-3">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                          {phase.name}
                        </h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          {phase.description}
                        </p>
                      </div>

                      <div className="space-y-2 ml-4">
                        {phase.prompts.map((promptName) => {
                          const promptData = processorPrompts.prompts[promptName];
                          if (!promptData) return null;

                          return (
                            <PromptItem
                              key={promptName}
                              promptName={promptName}
                              promptData={promptData}
                              currentContent={getCurrentPromptContent(promptName)}
                              onEdit={() => handleEditPrompt(promptName)}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tools Tab Content (SWRM-129) */}
          {activeTab === 'tools' && (
            <div>
              <div className="space-y-4">
                {/* Info Banner */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3 space-y-2">
                  <p className="text-sm text-blue-900 dark:text-blue-200">
                    Select which tools this processor can use. By default, all tools are available.
                    Restricting tools can help focus the processor's capabilities for specific tasks.
                  </p>
                  {selectedNode?.data.processor_name === 'orchestrator' && (
                    <p className="text-sm text-blue-900 dark:text-blue-200">
                      Sub-agent tools are always available in the orchestrator and don't need to be selected here.
                      Configure sub-agents in the Sub-Agents tab.
                    </p>
                  )}
                </div>

                {/* Tool Selector */}
                <ToolSelector
                  selectedTools={(() => {
                    try {
                      const config = JSON.parse(configJson);
                      return config.allowed_tools !== undefined ? config.allowed_tools : null;
                    } catch {
                      return null;
                    }
                  })()}
                  onChange={(tools) => {
                    if (!selectedNode) return;
                    
                    try {
                      const config = JSON.parse(configJson);
                      config.allowed_tools = tools;
                      const newConfigJson = JSON.stringify(config, null, 2);
                      setConfigJson(newConfigJson);
                      
                      // Update node immediately
                      updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, config },
                      });
                    } catch {
                      // If JSON is invalid, create new config
                      const newConfig = { allowed_tools: tools };
                      const newConfigJson = JSON.stringify(newConfig, null, 2);
                      setConfigJson(newConfigJson);
                      
                      // Update node immediately
                      updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, config: newConfig },
                      });
                    }
                  }}
                  processorType={selectedNode?.data.processor_name as 'agentic_task' | 'orchestrator' | 'vector'}
                />
              </div>
            </div>
          )}

          {/* Tasks Tab Content */}
          {activeTab === 'tasks' && (
            <div>
              <TasksEditor
                tasks={(() => {
                  try {
                    const config = JSON.parse(configJson);
                    return config.preset_tasks || [];
                  } catch {
                    return [];
                  }
                })()}
                onChange={(tasks) => {
                  if (!selectedNode) return;
                  
                  try {
                    const config = JSON.parse(configJson);
                    config.preset_tasks = tasks;
                    const newConfigJson = JSON.stringify(config, null, 2);
                    setConfigJson(newConfigJson);
                    
                    // Update node immediately
                    updateNode(selectedNode.id, {
                      data: { ...selectedNode.data, config },
                    });
                  } catch {
                    // If JSON is invalid, create new config with tasks
                    const newConfig = { preset_tasks: tasks };
                    const newConfigJson = JSON.stringify(newConfig, null, 2);
                    setConfigJson(newConfigJson);
                    
                    // Update node immediately
                    updateNode(selectedNode.id, {
                      data: { ...selectedNode.data, config: newConfig },
                    });
                  }
                }}
              />
            </div>
          )}

          {/* Commands Tab Content (SWRM-174) */}
          {activeTab === 'commands' && (
            <div>
              <ShellConfigHelper
                onConfigGenerated={(config) => {
                  // Update config JSON
                  setConfigJson(JSON.stringify(config, null, 2));
                  if (selectedNode) {
                    updateNode(selectedNode.id, {
                      data: { ...selectedNode.data, config },
                    });
                  }
                }}
                disabled={isLocked}
                selectedNode={selectedNode}
                updateNode={updateNode}
              />
            </div>
          )}

          {/* Vector Config Tab Content (SWRM-176) */}
          {activeTab === 'vector_config' && (
            <div>
              <VectorConfigHelper
                onConfigGenerated={(config) => {
                  // Update config JSON
                  setConfigJson(JSON.stringify(config, null, 2));
                  if (selectedNode) {
                    updateNode(selectedNode.id, {
                      data: { ...selectedNode.data, config },
                    });
                  }
                }}
                disabled={isLocked}
                selectedNode={selectedNode}
                updateNode={updateNode}
              />
            </div>
          )}
        </div>
      </div>

      {/* Prompt Editor Modal (SWRM-103) */}
      {editingPrompt && (
        <PromptEditorModal
          isOpen={true}
          promptName={editingPrompt.name}
          promptData={editingPrompt.data}
          currentContent={editingPrompt.currentContent}
          processorName={selectedNode?.data.processor_name}
          standardOutputKeys={selectedNode?.data.output_keys || []}
          onSave={handleSavePrompt}
          onClose={handleClosePromptEditor}
          onOutputsChange={handleDynamicOutputsChange}
        />
      )}
    </div>
  );
};

